import { Injectable, Optional } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, combineLatest, merge, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, skip, take, takeUntil, tap } from 'rxjs/operators';
import { AppService } from 'app/services/app.service';
import { DomainFilterService } from 'app/services/domain-filter.service';
import { WebSocketService } from 'app/services/websocket.service';
import { AssistantContextService } from './assistant-context.service';
import { AssistantMetaService } from './assistant-meta.service';
import {
	AssistantArea, IAssistantActionResponse, IAssistantAttachment, IAssistantChatResponse, IAssistantClientAction, IAssistantContextResponse, IAssistantEntityRef,
	IAssistantError, IAssistantMessage, IAssistantPendingAction, IAssistantReleaseResponse, IAssistantSuggestion,
	IAssistantSuggestionsResponse, IAssistantThread, IAssistantTurnStatus
} from './assistant.models';

/**
 * Stato del pannello assistente (root: sopravvive alla navigazione, la conversazione segue l'utente tra le pagine).
 * Il server ricostruisce il contesto a ogni turno; qui si tiene solo la vista.
 * entityChanged: emesso dopo una conferma che ha scritto dati → le pagine aperte su quell'entità si ricaricano.
 */
@Injectable({
	providedIn: 'root'
})
export class AssistantService{

	/** polling solo senza websocket: veloce all'inizio (la maggior parte dei turni), poi più rado */
	static readonly FAST_POLL_MS = 3000;
	static readonly SLOW_POLL_MS = 6000;
	static readonly FAST_POLL_FOR_MS = 30000;
	/** con websocket: un controllo ogni 2 minuti contro un evento perso */
	static readonly WATCHDOG_MS = 120000;
	static readonly JOIN_TIMEOUT_MS = 3000;

	readonly open = new BehaviorSubject<boolean>(false);
	readonly messages = new BehaviorSubject<IAssistantMessage[]>([]);
	readonly sending = new BehaviorSubject<boolean>(false);
	readonly serverContext = new BehaviorSubject<IAssistantContextResponse | null>(null);
	readonly entityChanged = new Subject<IAssistantEntityRef>();
	/** proposte ancora da confermare: pallino sul pulsante flottante */
	readonly pendingCount$: Observable<number>;
	/** l'entità aperta ha i prezzi gestiti dall'assistente: le UI manuali dei prezzi vanno in sola lettura */
	readonly pricingManaged$: Observable<boolean>;
	/** modulo ai_assistant attivo sul dominio selezionato (domain settings) */
	readonly available$: Observable<boolean>;
	/**
	 * Consigli scritti dal modello per l'entità aperta (key = type:id). items null = usare quelli a regole del context.
	 * Chiesti solo a pannello aperto e conversazione vuota: ogni richiesta può costare token (cache lato server).
	 */
	readonly smartSuggestions = new BehaviorSubject<{ key: string | null; loading: boolean; items: IAssistantSuggestion[] | null }>({ key: null, loading: false, items: null });

	private conversationId: string | null = null;
	private lastUserText: string | null = null;
	private lastAttachmentIds: string[] = [];
	private sequence = 0;
	/**
	 * Una conversazione per entità (type:id, "none" fuori dalle entità): cambiando pacchetto il pannello mostra solo la
	 * chat di quel pacchetto e la ritrova tornandoci. Solo in memoria (sessione); il server lega comunque ogni
	 * conversazione alla sua entità.
	 */
	private threads = new Map<string, IAssistantThread>();
	private threadKey = 'none';
	/** conversazioni con una risposta in arrivo: "sending" vale per la conversazione mostrata, non per tutte */
	private inFlight = new Set<string>();
	/** turni in coda seguiti per conversazione (key → stop): websocket + polling */
	private followers = new Map<string, () => void>();
	/**
	 * Procedura guidata della conversazione mostrata (es. trip_resources_create): nasce scrivendo su una scheda in
	 * creazione e la segue sul record salvato (attach), così il server sa che deve accompagnare l'utente step per step.
	 */
	private guided: string | null = null;
	/** record appena creato dallo step 1: quando la route ci arriva, la conversazione di creazione passa a lui */
	private pendingAttach: { fromKey: string; entity: IAssistantEntityRef } | null = null;
	private attaching = false;
	/** "Salva e vai a ..." premuto o go_to_step: arrivati allo step l'assistente riprende da solo (con la richiesta) */
	private continueFrom: { title: string | null; request?: string } | null = null;

	constructor(
		private http: HttpClient,
		private appService: AppService,
		private contextService: AssistantContextService,
		private domainFilterService: DomainFilterService,
		@Optional() private webSocketService?: WebSocketService,
		@Optional() private metaService?: AssistantMetaService
	){
		const domainSettingChange$ = merge(this.domainFilterService.filterDomainId, this.domainFilterService.domainSettingChangeEvent);
		const moduleEnabled$ = domainSettingChange$.pipe(
			// modulo attivo sul dominio e utente abilitato dall'amministratore (user_enabled assente = server precedente)
			map(() => !!this.domainFilterService.getDomainSetting('current', 'enabled_modules.ai_assistant.enabled')
				&& this.domainFilterService.getDomainSetting('current', 'enabled_modules.ai_assistant.user_enabled') !== false),
			distinctUntilChanged()
		);
		// modulo attivo E un punto della UI ancorato (AssistantContextService.anchor) la cui area è attiva per il dominio
		this.available$ = combineLatest([moduleEnabled$, this.contextService.anchoredAreas, domainSettingChange$]).pipe(
			map(([enabled, areas]) => enabled && areas.some(area => this.isAreaEnabled(area))),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: false })
		);
		// chiusura solo se l'assistente resta non disponibile: cambiando pagina (es. creazione → modifica dopo il
		// salvataggio) i punti ancorati si rilasciano e si riancorano subito dopo, il pannello deve restare aperto
		this.available$.pipe(debounceTime(400), filter(available => !available)).subscribe(() => this.open.next(false));
		// aree, icone e frasi d'apertura: servono appena il modulo è attivo
		moduleEnabled$.pipe(filter(Boolean), take(1)).subscribe(() => this.metaService?.load());

		this.pendingCount$ = this.messages.pipe(
			map(messages => messages.filter(message => message.action?.status === 'pending').length),
			distinctUntilChanged()
		);

		// cambio azienda: si azzerano tutte le conversazioni (il budget e i dati sono di un'altra azienda)
		this.domainFilterService.filterDomainId.pipe(distinctUntilChanged(), skip(1)).subscribe(() => {
			this.stopFollowing();
			this.threads.clear();
			this.newConversation();
		});

		// cambio pacchetto: si passa alla conversazione di quel pacchetto (scheda in creazione: una per tipo)
		this.contextService.entityCreated.subscribe(entity => this.pendingAttach = { fromKey: this.threadKey, entity });
		combineLatest([this.contextService.entity, this.contextService.newEntityType]).pipe(
			map(([entity, newEntityType]) => entity ? AssistantService.threadKeyOf(entity) : newEntityType ? 'new:' + newEntityType : 'none'),
			distinctUntilChanged()
		).subscribe(key => this.switchThread(key));
		this.contextService.step.pipe(filter(Boolean)).subscribe(() => this.continueOnNewStep());

		// contesto server (entità, prezzi gestiti dall'assistente) anche a pannello chiuso: serve al blocco della UI manuale
		// riletto anche a ogni nuova schermata prezzi aperta: il segno può essere cambiato in un'altra scheda/da un altro utente
		merge(
			combineLatest([this.contextService.entity, this.domainFilterService.filterDomainId, this.contextService.anchored, this.contextService.focusArea]).pipe(
				filter(([, , anchored]) => anchored)
			),
			this.contextService.anchorAdded
		).pipe(debounceTime(100)).subscribe(() => this.refreshContext());

		combineLatest([this.open, this.serverContext]).pipe(
			filter(([open, context]) => open && !!context?.available && !!context.entity && !this.messages.value.length),
			debounceTime(150)
		).subscribe(([, context]) => this.loadSmartSuggestions(context!.entity!));

		this.pricingManaged$ = combineLatest([this.serverContext, this.contextService.entity]).pipe(
			map(([context, entity]) => !!(context?.available && entity && context.entity
				&& context.entity.type === entity.type && context.entity.id === entity.id
				&& context.entity.pricing_managed_by_assistant)),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: false })
		);
	}

	toggle(): void{
		this.open.next(!this.open.value);
	}

	/**
	 * Apertura automatica (IAssistantAnchorConfig.autoOpen): appena l'area ancorata è disponibile per il dominio; se non
	 * lo diventa a breve (modulo o area spenti) non si apre.
	 */
	openWhenAvailable(): void{
		this.available$.pipe(filter(Boolean), take(1), takeUntil(timer(5000))).subscribe(() => this.open.next(true));
	}

	newConversation(): void{
		this.conversationId = null;
		this.lastUserText = null;
		this.lastAttachmentIds = [];
		this.guided = null;
		this.continueFrom = null;
		this.threads.delete(this.threadKey);
		this.messages.next([]);
	}

	/** frase d'apertura della sezione (assistant/meta); null = metadati non ancora arrivati */
	areaIntro(area: string | null | undefined): string | null{
		return this.metaService?.area(area)?.intro ?? null;
	}

	suggestionIcon(kind: string): string{
		return this.metaService?.icon(kind) ?? 'north_east';
	}

	/** switch dell'area nelle impostazioni del dominio selezionato; assente = attiva (server precedente agli switch) */
	isAreaEnabled(area: AssistantArea): boolean{
		return this.domainFilterService.getDomainSetting('current', 'enabled_modules.ai_assistant.areas.' + area) !== false;
	}

	/** consigli per entità e sezione aperta: il server scrive consigli col modello solo in quella dei prezzi */
	smartSuggestionsKey(entity: IAssistantEntityRef | null): string | null{
		return entity ? entity.type + ':' + entity.id + ':' + (this.contextService.current().area || '') : null;
	}

	static threadKeyOf(entity: IAssistantEntityRef | null): string{
		return entity ? entity.type + ':' + entity.id : 'none';
	}

	private switchThread(key: string): void{
		if(key === this.threadKey) return;
		// salvataggio della scheda in creazione: stessa conversazione sul record appena creato
		const attach = this.pendingAttach && this.pendingAttach.fromKey === this.threadKey && AssistantService.threadKeyOf(this.pendingAttach.entity) === key
			? this.pendingAttach : null;
		this.pendingAttach = null;
		if(attach && this.conversationId && !this.inFlight.has(this.threadKey)){
			this.threads.delete(this.threadKey);
			this.threadKey = key;
			this.attachConversation(attach.entity);
			return;
		}
		if(this.messages.value.length || this.conversationId){
			this.threads.set(this.threadKey, { conversationId: this.conversationId, lastUserText: this.lastUserText, lastAttachmentIds: this.lastAttachmentIds, messages: this.messages.value, guided: this.guided });
		}
		const next = this.threads.get(key);
		this.threadKey = key;
		this.conversationId = next?.conversationId ?? null;
		this.lastUserText = next?.lastUserText ?? null;
		this.lastAttachmentIds = next?.lastAttachmentIds ?? [];
		this.guided = next?.guided ?? null;
		this.continueFrom = null;
		this.messages.next(next?.messages ?? []);
		this.sending.next(this.inFlight.has(key));
	}

	private attachConversation(entity: IAssistantEntityRef): void{
		const key = this.threadKey;
		this.attaching = true;
		this.http.post<{ conversation_id: string }>(this.url('/assistant/conversations/' + this.conversationId + '/attach'), { entity }).subscribe({
			next: () => {
				this.attaching = false;
				if(key !== this.threadKey) return;
				this.refreshContext();
				this.continueOnNewStep();
			},
			// la storia resta a schermo ma il server aprirà una conversazione nuova sul record: si riparte da lì
			error: () => {
				this.attaching = false;
				if(key !== this.threadKey) return;
				this.conversationId = null;
				this.continueFrom = null;
			}
		});
	}

	/**
	 * Pulsante di un'azione lato client (next_step): salva lo step aperto e apre il successivo; arrivati lì
	 * l'assistente riprende con un messaggio automatico.
	 */
	runClientAction(item: IAssistantMessage): void{
		if(!item.clientAction || item.clientActionDone || this.sending.value) return;
		this.patch(item, { clientActionDone: true });
		this.continueFrom = { title: this.contextService.step.value?.title ?? null };
		this.contextService.stepCommand.next(item.clientAction);
	}

	/** se il salvataggio fallisce lo step non cambia: il pulsante torna usabile */
	clientActionFailed(): void{
		this.continueFrom = null;
		const item = [...this.messages.value].reverse().find(message => message.clientAction?.type === 'next_step' && message.clientActionDone);
		if(item) this.patch(item, { clientActionDone: false });
	}

	/**
	 * Procedura guidata: confermata l'ultima proposta in sospeso, il pannello offre subito "Salva e vai a <step>"
	 * (senza un turno del modello: la conferma non ne avvia uno).
	 */
	private offerNextStep(formFilled: boolean): void{
		const step = this.contextService.step.value;
		if(!this.guided || !step?.next_title) return;
		const messages = this.messages.value;
		if(messages.some(message => message.action?.status === 'pending')) return;
		if(messages.some(message => message.clientAction?.type === 'next_step' && !message.clientActionDone)) return;
		this.push({
			role: 'assistant',
			text: '',
			clientAction: {
				type: 'next_step',
				label: 'Salva e vai a ' + step.next_title,
				reason: formFilled ? 'Campi compilati: controllali, poi salva e continua.' : 'Modifica applicata: se questo step è a posto, continua.'
			}
		});
	}

	private continueOnNewStep(): void{
		const step = this.contextService.step.value;
		if(!this.continueFrom || this.attaching || !step || step.title === this.continueFrom.title || this.sending.value) return;
		const request = this.continueFrom.request;
		this.continueFrom = null;
		this.send(request
			? 'Sono allo step "' + step.title + '": proponimi qui quello che ti ho chiesto (' + request + ').'
			: 'Sono allo step "' + step.title + '": continuiamo da qui.');
	}

	refreshContext(): void{
		const context = this.contextService.current();
		let params = new HttpParams().set('route', context.route);
		if(context.area) params = params.set('area', context.area);
		if(context.entity){
			params = params.set('entity[type]', context.entity.type);
			if(context.entity.id) params = params.set('entity[id]', context.entity.id);
		}
		this.http.get<IAssistantContextResponse>(this.url('/assistant/context'), { params }).subscribe({
			next: response => this.serverContext.next(response),
			error: error => this.serverContext.next({ available: false, error: AssistantService.toError(error) })
		});
	}

	private loadSmartSuggestions(entity: IAssistantEntityRef): void{
		const context = this.contextService.current();
		const key = this.smartSuggestionsKey(entity);
		const current = this.smartSuggestions.value;
		if(current.key === key && (current.loading || current.items)) return;
		this.smartSuggestions.next({ key, loading: true, items: null });
		let params = new HttpParams().set('route', context.route).set('entity[type]', entity.type).set('entity[id]', entity.id);
		if(context.area) params = params.set('area', context.area);
		this.http.get<IAssistantSuggestionsResponse>(this.url('/assistant/suggestions'), { params }).subscribe({
			next: response => {
				if(this.smartSuggestions.value.key !== key) return;
				this.smartSuggestions.next({ key, loading: false, items: response.source === 'model' && response.suggestions?.length ? response.suggestions : null });
			},
			// i consigli a regole restano: nessun errore mostrato
			error: () => {
				if(this.smartSuggestions.value.key === key) this.smartSuggestions.next({ key, loading: false, items: null });
			}
		});
	}

	send(text: string, attachments: IAssistantAttachment[] = []): void{
		const message = text.trim();
		if((!message && !attachments.length) || this.sending.value) return;
		this.lastUserText = message;
		this.lastAttachmentIds = attachments.map(attachment => attachment.id);
		this.push({ role: 'user', text: message, ...(attachments.length ? { attachments } : {}) });
		this.request(message, this.lastAttachmentIds);
	}

	/**
	 * Ripete l'ultima richiesta (es. dopo rate_limited), senza duplicare il messaggio utente. Gli allegati già agganciati
	 * a un messaggio della conversazione il server li ignora (sono già nella storia).
	 */
	retry(): void{
		if(this.lastUserText === null || this.sending.value) return;
		this.messages.next(this.messages.value.filter(item => item.role !== 'notice'));
		this.request(this.lastUserText, this.lastAttachmentIds);
	}

	/**
	 * Carica un file con istruzioni: il server ne estrae subito il testo (errori di formato/lunghezza prima dell'invio).
	 */
	uploadAttachment(file: File): Observable<IAssistantAttachment>{
		const context = this.contextService.current();
		const form = new FormData();
		form.append('file', file, file.name);
		form.append('route', context.route);
		if(context.entity){
			form.append('entity[type]', context.entity.type);
			if(context.entity.id) form.append('entity[id]', context.entity.id);
		}
		return this.http.post<IAssistantAttachment>(this.url('/assistant/attachments'), form);
	}

	/**
	 * "Torna alla gestione manuale" sul pacchetto: i prezzi tornano modificabili dalla UI.
	 */
	releasePricing(entity: IAssistantEntityRef): Observable<IAssistantReleaseResponse>{
		return this.http.post<IAssistantReleaseResponse>(this.url('/assistant/' + entity.type + '/' + entity.id + '/release-pricing'), {}).pipe(
			tap(response => {
				this.serverContext.next({ available: true, ...response.context });
				this.entityChanged.next(entity);
			})
		);
	}

	confirm(item: IAssistantMessage): void{
		this.handleAction(item, 'confirm');
	}

	cancel(item: IAssistantMessage): void{
		this.handleAction(item, 'cancel');
	}

	private request(message: string, attachmentIds: string[] = []): void{
		// la risposta va nella conversazione da cui è partita, anche se nel frattempo l'utente ha cambiato pacchetto
		const key = this.threadKey;
		this.inFlight.add(key);
		this.sending.next(true);
		// prima richiesta su una scheda in creazione: la conversazione diventa una procedura guidata
		if(key.startsWith('new:') && !this.guided) this.guided = key.slice(4) + '_create';
		this.http.post<IAssistantChatResponse>(this.url('/assistant/chat'), {
			conversation_id: this.conversationId,
			message,
			...(attachmentIds.length ? { attachment_ids: attachmentIds } : {}),
			context: { ...this.contextService.current(), ...(this.guided ? { guided: this.guided } : {}) }
		}).subscribe({
			next: response => {
				if(response.turn_id && !AssistantService.isFinished(response.status)){
					if(key === this.threadKey) this.conversationId = response.conversation_id;
					else this.storedThread(key).conversationId = response.conversation_id;
					if(response.context && key === this.threadKey) this.serverContext.next({ available: true, ...response.context });
					this.follow(key, response.turn_id);
					return;
				}
				this.deliver(key, response);
			},
			error: error => this.fail(key, AssistantService.toError(error))
		});
	}

	/**
	 * Chat in coda. Websocket funzionante (socket connesso e canale personale confermato dal server con l'ack di
	 * join-user) → si aspetta l'evento assistantTurn con la risposta completa, più un solo controllo di sicurezza ogni
	 * WATCHDOG_MS. Websocket assente, non confermato o disconnesso → polling di GET assistant/turns/{id}; al ritorno
	 * della connessione una lettura (eventi persi) e di nuovo solo websocket. Eventi su nomi propri (user-event): gli
	 * off('channel-event') degli altri componenti non toccano questi listener.
	 */
	private follow(key: string, turnId: string): void{
		const socket = this.webSocketService?.ioSocket;
		const startedAt = Date.now();
		let timer: ReturnType<typeof setTimeout> | null = null;
		let fetching = false;
		let stopped = false;
		let viaSocket = false;

		const clearTimer = () => { if(timer){ clearTimeout(timer); timer = null; } };
		const stop = () => {
			stopped = true;
			clearTimer();
			socket?.off('user-event', onUserEvent);
			socket?.off('disconnect', onDisconnect);
			socket?.off('connect', onConnect);
			this.followers.delete(key);
		};
		const finish = (turn: IAssistantChatResponse) => {
			stop();
			this.deliver(key, turn);
		};
		const schedule = () => {
			clearTimer();
			if(stopped) return;
			const delay = viaSocket ? AssistantService.WATCHDOG_MS
				: Date.now() - startedAt < AssistantService.FAST_POLL_FOR_MS ? AssistantService.FAST_POLL_MS : AssistantService.SLOW_POLL_MS;
			timer = setTimeout(check, delay);
		};
		const check = () => {
			if(stopped || fetching) return;
			fetching = true;
			this.http.get<IAssistantChatResponse>(this.url('/assistant/turns/' + turnId)).subscribe({
				next: turn => {
					fetching = false;
					if(stopped) return;
					if(AssistantService.isFinished(turn.status)) finish(turn);
					else schedule();
				},
				error: error => {
					fetching = false;
					if(stopped) return;
					// turno scaduto o non più accessibile: inutile insistere
					if(error?.status === 404 || error?.status === 403){
						stop();
						this.fail(key, AssistantService.toError(error));
						return;
					}
					schedule();
				}
			});
		};
		const useSocket = (joined: boolean, readNow: boolean) => {
			if(stopped) return;
			viaSocket = joined;
			if(readNow) check();
			else schedule();
		};
		const onUserEvent = (payload: { event?: string; data?: IAssistantChatResponse }) => {
			if(payload?.event === 'assistantTurn' && payload.data?.turn_id === turnId && AssistantService.isFinished(payload.data.status)) finish(payload.data);
		};
		const onDisconnect = () => useSocket(false, false);
		// riconnessione: eventi persi nel frattempo → una lettura, poi di nuovo websocket se il canale è confermato
		const onConnect = () => this.joinUserChannel(joined => useSocket(joined, true));

		this.followers.set(key, stop);
		socket?.on('user-event', onUserEvent);
		socket?.on('disconnect', onDisconnect);
		socket?.on('connect', onConnect);
		this.joinUserChannel(joined => useSocket(joined, false));
	}

	/**
	 * Canale personale (private-user.{id}) nel server websocket: il server lo assegna dall'utente autenticato del
	 * socket e conferma con l'ack. Senza ack entro JOIN_TIMEOUT_MS (server che non lo supporta) → false = polling.
	 */
	private joinUserChannel(done: (joined: boolean) => void): void{
		const socket = this.webSocketService?.ioSocket;
		if(!socket?.connected){
			done(false);
			return;
		}
		let answered = false;
		const timeout = setTimeout(() => {
			if(answered) return;
			answered = true;
			done(false);
		}, AssistantService.JOIN_TIMEOUT_MS);
		socket.emit('join-user', (ack: { joined?: boolean }) => {
			if(answered) return;
			answered = true;
			clearTimeout(timeout);
			done(!!ack?.joined);
		});
	}

	private stopFollowing(): void{
		for (const [key, stop] of [...this.followers]) {
			stop();
			this.inFlight.delete(key);
		}
		this.sending.next(false);
	}

	private deliver(key: string, response: IAssistantChatResponse): void{
		if(response.status === 'failed'){
			if(key === this.threadKey && response.conversation_id) this.conversationId = response.conversation_id;
			this.fail(key, response.error ?? AssistantService.toError(new HttpErrorResponse({ status: 500 })));
			return;
		}
		this.inFlight.delete(key);
		if(key !== this.threadKey){
			this.deliverToStoredThread(key, response);
			return;
		}
		this.sending.next(false);
		this.conversationId = response.conversation_id;
		if(response.context) this.serverContext.next({ available: true, ...response.context });
		// proposte già mostrate e ritirate dall'assistente in questo turno: il loro cartellino non è più applicabile
		for (const updated of response.updated_actions || []) {
			const item = this.messages.value.find(message => message.action?.id === updated.id);
			if(item) this.patch(item, { action: updated });
		}
		// un'unica emissione: il pannello scorre all'inizio della risposta, non all'ultimo cartellino
		this.push(
			...(response.message ? [{ role: 'assistant' as const, text: response.message }] : []),
			...(response.pending_actions || []).map(action => ({ role: 'assistant' as const, text: '', action })),
			...AssistantService.clientActionMessages(response)
		);
		this.goToStep(response);
		this.openTab(response);
	}

	/** open_tab del turno: la scheda si apre subito (nessun pulsante) */
	private openTab(response: IAssistantChatResponse): void{
		const action = (response.client_actions || []).find((item: IAssistantClientAction) => item.type === 'open_tab');
		if(action?.type === 'open_tab') this.contextService.tabCommand.next({ tab: action.tab, lang: action.lang ?? null });
	}

	/** go_to_step del turno: lo step si apre subito, lì l'assistente riprende la richiesta (continueOnNewStep) */
	private goToStep(response: IAssistantChatResponse): void{
		const action = (response.client_actions || []).find((item: IAssistantClientAction) => item.type === 'go_to_step');
		if(action?.type !== 'go_to_step') return;
		this.continueFrom = { title: this.contextService.step.value?.title ?? null, request: action.request };
		this.contextService.stepCommand.next(action);
	}

	private fail(key: string, assistantError: IAssistantError): void{
		this.inFlight.delete(key);
		if(key !== this.threadKey){
			// la conversazione non è a schermo: l'errore la aspetta, come una risposta
			if(this.threads.has(key)){
				const thread = this.storedThread(key);
				thread.messages = [...thread.messages, { id: ++this.sequence, at: new Date(), role: 'notice', text: assistantError.message, errorCode: assistantError.code }];
			}
			return;
		}
		this.sending.next(false);
		this.push({ role: 'notice', text: assistantError.message, errorCode: assistantError.code });
	}

	private storedThread(key: string): IAssistantThread{
		if(!this.threads.has(key)) this.threads.set(key, { conversationId: null, lastUserText: null, messages: [] });
		return this.threads.get(key)!;
	}

	private deliverToStoredThread(key: string, response: IAssistantChatResponse): void{
		const thread = this.storedThread(key);
		const at = new Date();
		const updates = new Map((response.updated_actions || []).map(action => [action.id, action]));
		thread.conversationId = response.conversation_id;
		thread.messages = [
			...thread.messages.map(message => message.action && updates.has(message.action.id) ? { ...message, action: updates.get(message.action.id) } : message),
			...(response.message ? [{ id: ++this.sequence, at, role: 'assistant' as const, text: response.message }] : []),
			...(response.pending_actions || []).map(action => ({ id: ++this.sequence, at, role: 'assistant' as const, text: '', action })),
			...AssistantService.clientActionMessages(response).map(message => ({ id: ++this.sequence, at, ...message }))
		];
	}

	private handleAction(item: IAssistantMessage, operation: 'confirm' | 'cancel'): void{
		if(!item.action || item.busy || item.action.status !== 'pending') return;
		this.patch(item, { busy: true, actionError: undefined });

		this.http.post<IAssistantActionResponse>(this.url('/assistant/actions/' + item.action.id + '/' + operation), {}).subscribe({
			next: response => {
				this.patch(item, { busy: false, action: response.action, outcome: { at: new Date(), message: response.message } });
				// proposta lato client (es. compila il form): la esegue la scheda aperta, nessun dato salvato
				if(response.client_action) this.contextService.stepCommand.next(response.client_action);
				if(operation === 'confirm') this.offerNextStep(response.client_action?.type === 'fill_form');
				if(response.entity_changed){
					this.entityChanged.next(response.action.entity);
					// la prima conferma segna il pacchetto come gestito dall'assistente
					this.refreshContext();
				}
			},
			error: error => {
				const assistantError = AssistantService.toError(error);
				const status = AssistantService.statusFromError(assistantError.code);
				this.patch(item, {
					busy: false,
					actionError: assistantError.message,
					action: status ? { ...item.action, status } : item.action
				});
			}
		});
	}

	private push(...messages: Omit<IAssistantMessage, 'id' | 'at'>[]): void{
		if(!messages.length) return;
		const at = new Date();
		this.messages.next([...this.messages.value, ...messages.map(message => ({ id: ++this.sequence, at, ...message }))]);
	}

	private patch(item: IAssistantMessage, changes: Partial<IAssistantMessage>): void{
		this.messages.next(this.messages.value.map(message => message.id === item.id ? { ...message, ...changes } : message));
		Object.assign(item, changes);
	}

	private url(path: string): string{
		return this.appService.getBaseServerAddress() + path;
	}

	/**
	 * Errore mostrabile: {error: {code, message}} del server, validazione Laravel, rete.
	 */
	static toError(error: HttpErrorResponse): IAssistantError{
		const body = error?.error;
		if(body?.error?.code) return { code: body.error.code, message: body.error.message };
		// timeout di ingress/proxy (pagina HTML, non JSON): il server può aver completato comunque il turno
		if(error?.status === 504 || error?.status === 499) return { code: 'timeout', message: 'La risposta sta richiedendo più tempo del previsto e la connessione si è interrotta. Riprova tra poco: se la richiesta era complessa, prova a dividerla.' };
		if(error?.status === 0) return { code: 'network', message: 'Non riesco a contattare il server: controlla la connessione e riprova.' };
		if(error?.status === 422 && body?.message) return { code: 'invalid_request', message: body.message };
		if(error?.status === 403) return { code: 'forbidden', message: 'Non hai i permessi per questa operazione.' };
		return { code: 'unexpected', message: 'Si è verificato un errore imprevisto. Riprova tra poco.' };
	}

	/** pulsanti delle azioni lato client del turno (fill_form arriva solo dalla conferma di una proposta) */
	private static clientActionMessages(response: IAssistantChatResponse): Omit<IAssistantMessage, 'id' | 'at'>[]{
		return (response.client_actions || [])
			.filter((action: IAssistantClientAction) => action.type === 'next_step')
			.map(clientAction => ({ role: 'assistant' as const, text: '', clientAction }));
	}

	private static isFinished(status?: IAssistantTurnStatus): boolean{
		return status === 'completed' || status === 'failed';
	}

	private static statusFromError(code: string): IAssistantPendingAction['status'] | null{
		switch(code){
			case 'expired': return 'expired';
			case 'action_failed': return 'failed';
			default: return null;
		}
	}
}
